import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, CalendarDays, Users, Receipt, LogOut, Heart, Sparkles, MailOpen, AlertTriangle, UserPlus, CalendarRange, X, UserCheck, ScrollText, Globe, MoreHorizontal, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";

const linkBase =
  "flex items-center gap-3 px-4 py-3 text-sm tracking-wide transition-colors border-l-2 border-transparent";
const linkActive = "border-[#C88270] text-[#2A2421] bg-white";
const linkIdle = "text-[#5C5652] hover:text-[#2A2421] hover:bg-white/60";

function NavItem({ to, icon: Icon, label, testId, badge, badgeColor = "bg-[#C88270]" }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
    >
      <Icon size={18} strokeWidth={1.5} className="shrink-0" />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span data-testid={`${testId}-badge`} className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full ${badgeColor} text-white text-[10px] font-medium`}>
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// Context-aware FAB config per route
function fabFor(pathname, isAdmin) {
  if (isAdmin) {
    if (pathname === "/weddings" || pathname === "/leads") return { label: "New Lead", go: "/weddings?new=1" };
    if (pathname === "/events") return { label: "New Event", go: "/events?new=1" };
    if (pathname === "/trials") return { label: "New Trial", go: "/trials?new=1" };
  } else {
    if (pathname === "/expenses")
      return { label: "Add Expense", go: "/events", toastMsg: "Open an event you're assigned to, then add your expense." };
  }
  return null;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [pendingCount, setPendingCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [staffingCount, setStaffingCount] = useState(0);
  const [accessCount, setAccessCount] = useState(0);
  const [leadsCount, setLeadsCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const today = new Date().toISOString().slice(0, 10);
    const fetchCounts = async () => {
      try {
        if (isAdmin) {
          const [nres, eres, ares, lres] = await Promise.all([api.get("/notifications"), api.get("/events"), api.get("/access-requests"), api.get("/website-leads/new-count")]);
          const unread = nres.data.filter((n) => !n.read).length;
          const under = eres.data.filter(
            (ev) => ev.event_date >= today && (ev.assigned || []).filter((a) => a.status === "available").length < 2
          ).length;
          if (active) { setAlertCount(unread); setStaffingCount(under); setAccessCount(ares.data.length); setLeadsCount(lres.data.count || 0); }
        } else {
          const res = await api.get("/events");
          const count = res.data.filter(
            (ev) => ev.event_date >= today &&
              (ev.assigned || []).some((a) => a.team_member_id === user.id && (a.status || "invited") === "invited")
          ).length;
          if (active) setPendingCount(count);
        }
      } catch (err) { console.debug("Nav badge count fetch failed", err); }
    };
    fetchCounts();
    window.addEventListener("invitations-updated", fetchCounts);
    window.addEventListener("admin-data-updated", fetchCounts);
    return () => {
      active = false;
      window.removeEventListener("invitations-updated", fetchCounts);
      window.removeEventListener("admin-data-updated", fetchCounts);
    };
  }, [isAdmin, user, location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // ---- Mobile bottom-nav model ----
  const isActivePath = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
  const primaryTabs = isAdmin
    ? [
        { to: "/dashboard", icon: LayoutDashboard, label: "Home", testId: "bottom-nav-dashboard" },
        { to: "/events", icon: CalendarDays, label: "Events", testId: "bottom-nav-events" },
        { to: "/leads", icon: Globe, label: "Leads", testId: "bottom-nav-leads", badge: leadsCount },
        { to: "/calendar", icon: CalendarRange, label: "Calendar", testId: "bottom-nav-calendar" },
      ]
    : [
        { to: "/dashboard", icon: LayoutDashboard, label: "Home", testId: "bottom-nav-dashboard" },
        { to: "/invitations", icon: MailOpen, label: "Invites", testId: "bottom-nav-invitations", badge: pendingCount },
        { to: "/events", icon: CalendarDays, label: "Events", testId: "bottom-nav-events" },
        { to: "/calendar", icon: CalendarRange, label: "Calendar", testId: "bottom-nav-calendar" },
      ];

  const moreItems = isAdmin
    ? [
        { to: "/weddings", icon: Heart, label: "Weddings", testId: "more-nav-weddings" },
        { to: "/quotations", icon: ScrollText, label: "Quotation Studio", testId: "more-nav-quotations" },
        { to: "/alerts", icon: AlertTriangle, label: "Reassignment Alerts", testId: "more-nav-alerts", badge: alertCount, badgeColor: "bg-red-600" },
        { to: "/staffing", icon: UserPlus, label: "Needs More Team", testId: "more-nav-staffing", badge: staffingCount, badgeColor: "bg-[#D4A373]" },
        { to: "/trials", icon: Sparkles, label: "Trials", testId: "more-nav-trials" },
        { to: "/expenses", icon: Receipt, label: "Expenses", testId: "more-nav-expenses" },
        { to: "/team", icon: Users, label: "Team", testId: "more-nav-team" },
        { to: "/access-requests", icon: UserCheck, label: "Access Requests", testId: "more-nav-access-requests", badge: accessCount, badgeColor: "bg-[#829384]" },
      ]
    : [
        { to: "/trials", icon: Sparkles, label: "Trials", testId: "more-nav-trials" },
        { to: "/expenses", icon: Receipt, label: "Expenses", testId: "more-nav-expenses" },
      ];

  const moreBadgeTotal = moreItems.reduce((s, i) => s + (i.badge || 0), 0);
  const fab = fabFor(location.pathname, isAdmin);
  const onFab = () => {
    if (!fab) return;
    if (fab.toastMsg) toast(fab.toastMsg);
    navigate(fab.go);
  };

  return (
    <div className="min-h-screen flex bg-[#FAF9F6]">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-[#EAE7E0] bg-[#FAF9F6]">
        <div className="px-6 py-8 border-b border-[#EAE7E0] flex items-center gap-3">
          <img src="/dejavu-mark.png?v=3" alt="Dejavu Weddings" className="w-11 h-11 object-contain shrink-0" />
          <div>
            <div className="font-serif text-3xl tracking-tight text-[#2A2421] leading-none">Dejavu</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mt-1">Weddings</div>
          </div>
        </div>
        <nav className="flex-1 py-6 flex flex-col gap-1">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
          {isAdmin && <NavItem to="/weddings" icon={Heart} label="Weddings" testId="nav-weddings" />}
          {isAdmin && <NavItem to="/quotations" icon={ScrollText} label="Quotation Studio" testId="nav-quotations" />}
          {isAdmin && <NavItem to="/leads" icon={Globe} label="Website Leads" testId="nav-leads" badge={leadsCount} badgeColor="bg-[#C88270]" />}
          {!isAdmin && <NavItem to="/invitations" icon={MailOpen} label="Invitations" testId="nav-invitations" badge={pendingCount} />}
          {isAdmin && <NavItem to="/alerts" icon={AlertTriangle} label="Reassignment Alerts" testId="nav-alerts" badge={alertCount} badgeColor="bg-red-600" />}
          {isAdmin && <NavItem to="/staffing" icon={UserPlus} label="Needs More Team" testId="nav-staffing" badge={staffingCount} badgeColor="bg-[#D4A373]" />}
          <NavItem to="/trials" icon={Sparkles} label="Trials" testId="nav-trials" />
          <NavItem to="/events" icon={CalendarDays} label="All Events" testId="nav-events" />
          <NavItem to="/calendar" icon={CalendarRange} label="Calendar" testId="nav-calendar" />
          <NavItem to="/expenses" icon={Receipt} label="Expenses" testId="nav-expenses" />
          {isAdmin && <NavItem to="/team" icon={Users} label="Team" testId="nav-team" />}
          {isAdmin && <NavItem to="/access-requests" icon={UserCheck} label="Access Requests" testId="nav-access-requests" badge={accessCount} badgeColor="bg-[#829384]" />}
        </nav>
        <div className="border-t border-[#EAE7E0] p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#5C5652] mb-1" data-testid="current-user-role">
            {isAdmin ? "Admin" : "Team Member"}
          </div>
          <div className="text-sm font-medium text-[#2A2421] mb-4" data-testid="current-user-name">
            {user?.name}
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C5652] hover:text-[#C88270] transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar (brand only) */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#FAF9F6]/90 backdrop-blur-xl border-b border-[#EAE7E0]">
        <div className="flex items-center justify-center px-4 py-3">
          <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="mobile-home-logo">
            <img src="/dejavu-mark.png?v=3" alt="Dejavu Weddings" className="w-7 h-7 object-contain" />
            <div className="font-serif text-2xl tracking-tight text-[#2A2421]">Dejavu Weddings</div>
          </NavLink>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 h-[68px] bg-[#FAF9F6]/95 backdrop-blur-xl border-t border-[#EAE7E0] flex items-stretch justify-around pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_24px_rgba(42,36,33,0.05)]" data-testid="mobile-bottom-nav">
        {primaryTabs.map((t) => {
          const active = isActivePath(t.to);
          return (
            <button key={t.to} onClick={() => navigate(t.to)} data-testid={t.testId}
              className={`relative flex flex-col items-center justify-center flex-1 gap-1 transition-colors duration-200 active:scale-95 ${active ? "text-[#C88270]" : "text-[#5C5652]"}`}>
              <t.icon size={22} strokeWidth={active ? 2 : 1.5} />
              <span className="text-[10px] font-semibold tracking-wide">{t.label}</span>
              {t.badge > 0 && (
                <span className="absolute top-2 right-[calc(50%-18px)] flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#C88270] px-1 text-[9px] font-bold text-white ring-2 ring-[#FAF9F6]">{t.badge}</span>
              )}
              {active && <span className="absolute top-0 h-[2px] w-8 rounded-full bg-[#C88270]" />}
            </button>
          );
        })}
        <button onClick={() => setMoreOpen(true)} data-testid="bottom-nav-more"
          className={`relative flex flex-col items-center justify-center flex-1 gap-1 transition-colors duration-200 active:scale-95 ${moreOpen ? "text-[#C88270]" : "text-[#5C5652]"}`}>
          <MoreHorizontal size={22} strokeWidth={1.5} />
          <span className="text-[10px] font-semibold tracking-wide">More</span>
          {moreBadgeTotal > 0 && (
            <span className="absolute top-2 right-[calc(50%-18px)] flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#C88270] px-1 text-[9px] font-bold text-white ring-2 ring-[#FAF9F6]">{moreBadgeTotal}</span>
          )}
        </button>
      </nav>

      {/* Context-aware FAB */}
      {fab && (
        <button onClick={onFab} data-testid="mobile-fab" aria-label={fab.label}
          className="md:hidden fixed right-5 bottom-[84px] z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#C88270] text-white shadow-[0_8px_20px_-4px_rgba(200,130,112,0.55)] transition-transform active:scale-90">
          <Plus size={26} strokeWidth={2} />
        </button>
      )}

      {/* Mobile "More" bottom sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-more-sheet">
          <div className="absolute inset-0 bg-[#2A2421]/40 backdrop-blur-sm" onClick={() => setMoreOpen(false)} data-testid="more-sheet-overlay" />
          <div className="absolute bottom-0 inset-x-0 bg-[#FAF9F6] rounded-t-[28px] border-t border-[#EAE7E0] shadow-[0_-8px_30px_rgba(42,36,33,0.12)] p-6 pb-8 animate-in slide-in-from-bottom duration-250 max-h-[80vh] overflow-y-auto">
            <div className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-[#EAE7E0]" />
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-[#2A2421]">More</h2>
              <button onClick={() => setMoreOpen(false)} data-testid="more-sheet-close" className="p-2 text-[#5C5652]"><X size={22} /></button>
            </div>
            <div className="grid grid-cols-1 divide-y divide-[#EAE7E0]/60">
              {moreItems.map((i) => (
                <button key={i.to} onClick={() => navigate(i.to)} data-testid={i.testId}
                  className="flex items-center gap-3 py-4 text-base font-medium text-[#2A2421] active:bg-[#EAE7E0]/30 rounded-lg px-2 transition-colors">
                  <i.icon size={20} strokeWidth={1.5} className="text-[#5C5652]" />
                  <span className="flex-1 text-left">{i.label}</span>
                  {i.badge > 0 && (
                    <span className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full ${i.badgeColor || "bg-[#C88270]"} text-white text-[10px] font-bold`}>{i.badge}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-[#EAE7E0] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">{isAdmin ? "Admin" : "Team Member"}</div>
                <div className="text-sm font-medium text-[#2A2421]">{user?.name}</div>
              </div>
              <button onClick={onLogout} data-testid="mobile-logout-button"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C5652] hover:text-[#C88270] transition-colors">
                <LogOut size={16} strokeWidth={1.5} /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 pb-24 md:pt-0 md:pb-0" data-testid="app-main">
        <Outlet />
      </main>
    </div>
  );
}
